import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Spin, message, Card, Row, Col, Tag, Progress, Empty, Statistic, Tooltip, Collapse, Table } from 'antd';
import { 
  ArrowLeftOutlined, 
  BarChartOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  PercentageOutlined,
  AimOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  SafetyOutlined,
  InfoCircleOutlined,
  CaretRightOutlined
} from '@ant-design/icons';
import { judgeTaskExecutionApi } from '../../services/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';

const { Panel } = Collapse;

/**
 * 机审场景结果统计页面（业务场景ID不为12）
 * 支持 presetMetrics + customMetrics 数据结构
 */
function AutoReviewResultStats() {
  const { executionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const taskName = searchParams.get('taskName') || '';
  
  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState(null);
  const [executionInfo, setExecutionInfo] = useState(null);

  // 转换 presetMetrics 数据格式：兼容多种数据结构
  // 兼容多种数据格式：
  // 1. instance_stats 数组结构（新格式）-> 保留原结构，同时添加扁平化字段
  // 2. 扁平化结构（旧格式）-> 不需要转换，直接返回
  // 3. 空对象 -> 不需要转换，直接返回
  const transformPresetMetrics = (data) => {
    if (!data || !data.presetMetrics) return data;

    const transformed = { ...data };
    const newPresetMetrics = { ...data.presetMetrics };

    // 遍历所有维度
    Object.entries(newPresetMetrics).forEach(([dimensionKey, dimensionData]) => {
      // 跳过预设维度，保持原样
      if (['document', 'rule_avg', 'rule_detail', 'group_metrics'].includes(dimensionKey)) {
        return;
      }

      // 安全检查：确保 dimensionData 是对象
      if (!dimensionData || typeof dimensionData !== 'object') {
        return;
      }

      // 判断是否需要转换：只有当存在 instance_stats 数组且长度 > 0 时才处理
      const hasInstanceStats = Array.isArray(dimensionData.instance_stats) && 
                               dimensionData.instance_stats.length > 0;

      if (hasInstanceStats) {
        // 格式 1：instance_stats 数组结构（新格式）
        // 保留原结构，同时添加维度级汇总字段
        const firstInstance = dimensionData.instance_stats[0];
        
        // 安全检查：确保 firstInstance 是对象
        if (!firstInstance || typeof firstInstance !== 'object') {
          return;
        }

        // 保留完整的 instance_stats 数组，同时添加维度级字段
        // 注意：不要覆盖维度级别的 accuracy 等字段，它们才是整体汇总值
        newPresetMetrics[dimensionKey] = {
          // 保留完整的 instance_stats 数组（重要！）
          instance_stats: dimensionData.instance_stats,
          // 保留维度级元数据
          instance_count: dimensionData.instance_count,
          total: dimensionData.total,
          match_total: dimensionData.match_total,
          // 添加维度级 rule_stats（如果有的话）
          rule_stats: dimensionData.rule_stats || [],
          // 保留维度级别的指标值（这些是整体汇总值，不能被第一个instance覆盖）
          accuracy: dimensionData.accuracy,
          accuracy_value: dimensionData.accuracy_value,
          risk_precision: dimensionData.risk_precision,
          risk_precision_value: dimensionData.risk_precision_value,
          no_risk_precision: dimensionData.no_risk_precision,
          no_risk_precision_value: dimensionData.no_risk_precision_value,
          risk_recall: dimensionData.risk_recall,
          risk_recall_value: dimensionData.risk_recall_value,
          no_risk_recall: dimensionData.no_risk_recall,
          no_risk_recall_value: dimensionData.no_risk_recall_value,
          // 兼容旧代码：如果维度级别没有这些字段，才从第一个instance取
          instance_name: dimensionData.instance_name || firstInstance.instance_name,
          instance_code: dimensionData.instance_code || firstInstance.instance_code,
          rule_avg: dimensionData.rule_avg || firstInstance.rule_avg
        };
      }
      // 否则（格式 2、3）：已经是扁平化结构或空对象，不需要转换，保持原样
    });

    transformed.presetMetrics = newPresetMetrics;
    return transformed;
  };

  // 颜色配置
  const GRADIENT_COLORS = [
    { start: '#667eea', end: '#764ba2' },
    { start: '#4facfe', end: '#00f2fe' },
    { start: '#43e97b', end: '#38f9d7' },
    { start: '#fa709a', end: '#fee140' },
    { start: '#f093fb', end: '#f5576c' },
    { start: '#30cfd0', end: '#330867' }
  ];

  const COLORS = ['#5B8FF9', '#5AD8A6', '#5D7092', '#F6BD16', '#E86452', '#6DC8EC', '#945FB9', '#FF9845'];

  useEffect(() => {
    if (executionId) {
      fetchExecutionDetail();
    }
  }, [executionId]);

  const fetchExecutionDetail = async () => {
    setLoading(true);
    try {
      const response = await judgeTaskExecutionApi.getDetail(executionId);
      setExecutionInfo(response);
      
      // 解析 aleResult 字段
      if (response?.aleResult) {
        try {
          const parsedData = typeof response.aleResult === 'string'
            ? JSON.parse(response.aleResult)
            : response.aleResult;
          
          // 转换数据格式：将 instance_stats 数组结构扁平化
          const transformedData = transformPresetMetrics(parsedData);
          setStatsData(transformedData);
        } catch (parseError) {
          console.error('解析结果统计数据失败:', parseError);
          message.error('结果统计数据格式错误');
        }
      }
    } catch (error) {
      console.error('获取执行历史详情失败:', error);
      message.error('获取执行历史详情失败');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // 由于页面是通过 window.open 在新标签页打开的，navigate(-1) 没有历史记录
    // 尝试关闭当前标签页，如果无法关闭则跳转到任务执行历史页面
    if (window.opener) {
      // 有父窗口，关闭当前标签页
      window.close();
    } else {
      // 无父窗口，跳转到任务执行历史页面
      navigate('/judge-task', { state: { activeTab: 'execution' } });
    }
  };

  // 根据值获取颜色
  const getColorByRate = (rate) => {
    if (rate >= 0.9) return '#52c41a';
    if (rate >= 0.75) return '#1890FF';
    if (rate >= 0.6) return '#faad14';
    return '#ff4d4f';
  };

  // 预设指标名称映射（兼容指标定义格式）
  const PRESET_METRIC_NAMES = {
    risk_precision: '有风险精确率',
    no_risk_precision: '无风险精确率',
    risk_recall: '有风险召回率',
    no_risk_recall: '无风险召回率',
    accuracy: '准确率'
  };

  // 维度名称映射
  const DIMENSION_NAMES = {
    document: '单据维度',
    rule_detail: '规则明细维度',
    rule_avg: '规则平均维度'
  };

  // 渲染指标卡片
  const MetricCard = ({ name, value, numerator, denominator, index, dimension }) => {
    const gradient = GRADIENT_COLORS[index % GRADIENT_COLORS.length];
    const numericValue = typeof value === 'string' ? parseFloat(value) / 100 : value;
    
    return (
      <Card 
        style={{
          background: `linear-gradient(135deg, ${gradient.start} 0%, ${gradient.end} 100%)`,
          border: 'none',
          borderRadius: '16px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          height: '100%'
        }}
        bodyStyle={{ padding: '24px' }}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-start justify-between mb-3">
            <Tag color="rgba(255,255,255,0.3)" style={{ 
              border: '1px solid rgba(255,255,255,0.5)',
              color: 'white',
              fontWeight: 500
            }}>
              {dimension}
            </Tag>
            <Tooltip title={`${numerator || '-'} / ${denominator || '-'}`}>
              <BarChartOutlined style={{ fontSize: '20px', color: 'rgba(255,255,255,0.8)' }} />
            </Tooltip>
          </div>
          
          <div className="flex-1">
            <div style={{ color: 'white', fontSize: '14px', marginBottom: '8px', opacity: 0.9 }}>
              {name}
            </div>
            <div style={{ color: 'white', fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
              {typeof value === 'string' ? value : `${(numericValue * 100).toFixed(2)}%`}
            </div>
            {numerator !== undefined && denominator !== undefined && (
              <div style={{ color: 'white', fontSize: '12px', opacity: 0.8 }}>
                {numerator} / {denominator}
              </div>
            )}
          </div>
          
          <div style={{ marginTop: '16px' }}>
            <Progress 
              percent={numericValue * 100} 
              strokeColor="rgba(255,255,255,0.9)"
              trailColor="rgba(255,255,255,0.2)"
              showInfo={false}
              strokeWidth={6}
            />
          </div>
        </div>
      </Card>
    );
  };

  // 渲染维度指标
  const renderDimensionMetrics = (dimensionData, dimensionKey, dimensionName) => {
    if (!dimensionData) return null;

    const metrics = Object.entries(PRESET_METRIC_NAMES).map(([key, name], index) => {
      const value = dimensionData[key];
      const valueNum = dimensionData[`${key}_value`];
      if (value === undefined && valueNum === undefined) return null;
      
      return {
        key,
        name,
        value: value || `${(valueNum * 100).toFixed(2)}%`,
        numericValue: valueNum || (typeof value === 'string' ? parseFloat(value) / 100 : value),
        index
      };
    }).filter(Boolean);

    // 修改：即使没有指标字段，如果有rule_stats也要渲染
    if (metrics.length === 0 && !dimensionData.rule_stats) return null;

    return (
      <div style={{ marginBottom: '24px' }}>
        {/* 只有当有指标时才渲染维度标题和指标卡片 */}
        {metrics.length > 0 && (
          <>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', color: '#1a1a1a' }}>
              {dimensionKey === 'document' ? '📊' : dimensionKey === 'rule_avg' ? '🎯' : '📋'} {dimensionName}指标
            </h2>
            
            {dimensionKey === 'rule_avg' && (
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '16px 24px',
                borderRadius: '12px',
                marginBottom: '16px',
                color: 'white'
              }}>
                <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>
                  💡 维度说明
                </div>
                <div style={{ fontSize: '13px', lineHeight: '1.6', opacity: 0.85 }}>
                  以（单据 × 规则）为最小评估单元，各规则指标的平均值。
                  {dimensionData.rule_count && ` 共 ${dimensionData.rule_count} 条规则参与计算。`}
                </div>
              </div>
            )}

            <Row gutter={[16, 16]}>
              {metrics.map((metric, idx) => (
                <Col span={6} key={metric.key}>
                  <MetricCard 
                    name={metric.name}
                    value={metric.value}
                    numerator={dimensionData[`match_${metric.key.includes('risk') && !metric.key.includes('no_') ? 'risk' : metric.key.includes('no_risk') ? 'no_risk' : 'total'}`]}
                    denominator={dimensionData[metric.key.includes('precision') ? `machine_${metric.key.includes('no_risk') ? 'no_risk' : 'risk'}` : 
                                metric.key.includes('recall') ? `expected_${metric.key.includes('no_risk') ? 'no_risk' : 'risk'}` : 'total']}
                    index={idx + (dimensionKey === 'rule_avg' ? 5 : dimensionKey === 'rule_detail' ? 10 : 0)}
                    dimension={dimensionName}
                  />
                </Col>
              ))}
            </Row>
          </>
        )}

        {/* 规则平均维度的规则明细统计 */}
        {dimensionKey === 'rule_avg' && dimensionData.rule_stats && dimensionData.rule_stats.length > 0 && (
          <Card
            title={<span style={{ fontSize: '16px', fontWeight: 'bold' }}>规则详细统计</span>}
            style={{ marginTop: '16px', borderRadius: '12px' }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '16px 24px',
              borderRadius: '12px',
              marginBottom: '16px',
              color: 'white'
            }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>
                💡 维度说明
              </div>
              <div style={{ fontSize: '13px', lineHeight: '1.6', opacity: 0.85 }}>
                以（单据 × 规则）为最小评估单元，各规则指标的平均值。
                {dimensionData.rule_count && ` 共 ${dimensionData.rule_count} 条规则参与计算。`}
              </div>
            </div>
            
            <div className="space-y-4">
              {dimensionData.rule_stats.map((rule, index) => (
                <div 
                  key={rule.rule_code || index}
                  style={{
                    background: '#fafafa',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: COLORS[index % COLORS.length]
                      }} />
                      <span style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>
                        {rule.rule_name || rule.rule_code}
                      </span>
                    </div>
                    {rule.accuracy && (
                      <span style={{ 
                        fontSize: '24px', 
                        fontWeight: 'bold', 
                        color: getColorByRate(parseFloat(rule.accuracy?.replace('%', '') || 0) / 100)
                      }}>
                        {rule.accuracy}
                      </span>
                    )}
                  </div>
                  
                  {/* 动态展示存在的指标卡片 */}
                  {(() => {
                    const metricCards = [];
                    
                    if (rule.risk_precision) {
                      metricCards.push(
                        <div key="risk_precision" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>有风险精确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
                            {rule.risk_precision}
                          </div>
                        </div>
                      );
                    }
                    
                    if (rule.no_risk_precision) {
                      metricCards.push(
                        <div key="no_risk_precision" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>无风险精确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                            {rule.no_risk_precision}
                          </div>
                        </div>
                      );
                    }
                    
                    if (rule.risk_recall) {
                      metricCards.push(
                        <div key="risk_recall" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>有风险召回率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#faad14' }}>
                            {rule.risk_recall}
                          </div>
                        </div>
                      );
                    }
                    
                    if (rule.no_risk_recall) {
                      metricCards.push(
                        <div key="no_risk_recall" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>无风险召回率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#722ed1' }}>
                            {rule.no_risk_recall}
                          </div>
                        </div>
                      );
                    }
                    
                    if (rule.accuracy) {
                      metricCards.push(
                        <div key="accuracy" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>准确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#13c2c2' }}>
                            {rule.accuracy}
                          </div>
                        </div>
                      );
                    }
                    
                    if (metricCards.length === 0) return null;
                    
                    const cols = metricCards.length <= 3 ? metricCards.length : 5;
                    return (
                      <div className={`grid gap-3`} style={{ 
                        fontSize: '13px',
                        gridTemplateColumns: `repeat(${Math.min(cols, metricCards.length)}, 1fr)`
                      }}>
                        {metricCards}
                      </div>
                    );
                  })()}
                  
                  {/* 动态展示存在的总数信息 */}
                  {(() => {
                    const stats = [];
                    
                    if (rule.total !== undefined) {
                      stats.push(
                        <span key="total">总数: <strong style={{ color: '#262626' }}>{rule.total}</strong></span>
                      );
                    }
                    
                    if (rule.machine_risky !== undefined) {
                      stats.push(
                        <span key="machine_risky">机审有风险: <strong style={{ color: '#52c41a' }}>{rule.machine_risky}</strong></span>
                      );
                    }
                    
                    if (rule.machine_non_risky !== undefined) {
                      stats.push(
                        <span key="machine_non_risky">机审无风险: <strong style={{ color: '#1890ff' }}>{rule.machine_non_risky}</strong></span>
                      );
                    }
                    
                    if (rule.expected_risky !== undefined) {
                      stats.push(
                        <span key="expected_risky">人审有风险: <strong style={{ color: '#fa541c' }}>{rule.expected_risky}</strong></span>
                      );
                    }
                    
                    if (rule.expected_non_risky !== undefined) {
                      stats.push(
                        <span key="expected_non_risky">人审无风险: <strong style={{ color: '#722ed1' }}>{rule.expected_non_risky}</strong></span>
                      );
                    }
                    
                    if (stats.length === 0) return null;
                    
                    return (
                      <div className="flex items-center gap-4 mt-3" style={{ fontSize: '13px', color: '#8c8c8c' }}>
                        {stats}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* 规则明细维度的规则统计 */}
        {dimensionKey === 'rule_detail' && dimensionData.rule_stats && dimensionData.rule_stats.length > 0 && (
          <Card
            title={<span style={{ fontSize: '16px', fontWeight: 'bold' }}>规则详细统计</span>}
            style={{ marginTop: '16px', borderRadius: '12px' }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              padding: '16px 24px',
              borderRadius: '12px',
              marginBottom: '16px',
              color: 'white'
            }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>
                💡 维度说明
              </div>
              <div style={{ fontSize: '13px', lineHeight: '1.6', opacity: 0.85 }}>
                按规则明细维度统计，展示每个规则的具体指标表现。
                {dimensionData.rule_count && ` 共 ${dimensionData.rule_count} 条规则。`}
              </div>
            </div>
            
            <div className="space-y-4">
              {dimensionData.rule_stats.map((rule, index) => (
                <div 
                  key={rule.rule_code || index}
                  style={{
                    background: '#fafafa',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: COLORS[index % COLORS.length]
                      }} />
                      <span style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>
                        {rule.rule_name || rule.rule_code}
                      </span>
                    </div>
                    {rule.accuracy && (
                      <span style={{ 
                        fontSize: '24px', 
                        fontWeight: 'bold', 
                        color: getColorByRate(parseFloat(rule.accuracy?.replace('%', '') || 0) / 100)
                      }}>
                        {rule.accuracy}
                      </span>
                    )}
                  </div>
                  
                  {/* 动态展示存在的指标卡片 */}
                  {(() => {
                    const metricCards = [];
                    
                    if (rule.risk_precision) {
                      metricCards.push(
                        <div key="risk_precision" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>有风险精确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
                            {rule.risk_precision}
                          </div>
                        </div>
                      );
                    }
                    
                    if (rule.no_risk_precision) {
                      metricCards.push(
                        <div key="no_risk_precision" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>无风险精确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                            {rule.no_risk_precision}
                          </div>
                        </div>
                      );
                    }
                    
                    if (rule.risk_recall) {
                      metricCards.push(
                        <div key="risk_recall" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>有风险召回率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#faad14' }}>
                            {rule.risk_recall}
                          </div>
                        </div>
                      );
                    }
                    
                    if (rule.no_risk_recall) {
                      metricCards.push(
                        <div key="no_risk_recall" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>无风险召回率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#722ed1' }}>
                            {rule.no_risk_recall}
                          </div>
                        </div>
                      );
                    }
                    
                    if (rule.accuracy) {
                      metricCards.push(
                        <div key="accuracy" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>准确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#13c2c2' }}>
                            {rule.accuracy}
                          </div>
                        </div>
                      );
                    }
                    
                    if (metricCards.length === 0) return null;
                    
                    const cols = metricCards.length <= 3 ? metricCards.length : 5;
                    return (
                      <div className={`grid gap-3`} style={{ 
                        fontSize: '13px',
                        gridTemplateColumns: `repeat(${Math.min(cols, metricCards.length)}, 1fr)`
                      }}>
                        {metricCards}
                      </div>
                    );
                  })()}
                  
                  {/* 动态展示存在的总数信息 */}
                  {(() => {
                    const stats = [];
                    
                    if (rule.total !== undefined) {
                      stats.push(
                        <span key="total">总数: <strong style={{ color: '#262626' }}>{rule.total}</strong></span>
                      );
                    }
                    
                    if (rule.machine_risky !== undefined) {
                      stats.push(
                        <span key="machine_risky">机审有风险: <strong style={{ color: '#52c41a' }}>{rule.machine_risky}</strong></span>
                      );
                    }
                    
                    if (rule.machine_non_risky !== undefined) {
                      stats.push(
                        <span key="machine_non_risky">机审无风险: <strong style={{ color: '#1890ff' }}>{rule.machine_non_risky}</strong></span>
                      );
                    }
                    
                    if (rule.expected_risky !== undefined) {
                      stats.push(
                        <span key="expected_risky">人审有风险: <strong style={{ color: '#fa541c' }}>{rule.expected_risky}</strong></span>
                      );
                    }
                    
                    if (rule.expected_non_risky !== undefined) {
                      stats.push(
                        <span key="expected_non_risky">人审无风险: <strong style={{ color: '#722ed1' }}>{rule.expected_non_risky}</strong></span>
                      );
                    }
                    
                    if (stats.length === 0) return null;
                    
                    return (
                      <div className="flex items-center gap-4 mt-3" style={{ fontSize: '13px', color: '#8c8c8c' }}>
                        {stats}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* 自定义维度的规则统计 */}
        {!['document', 'rule_avg', 'rule_detail'].includes(dimensionKey) && dimensionData.rule_stats && dimensionData.rule_stats.length > 0 && (
          <Card
            title={<span style={{ fontSize: '16px', fontWeight: 'bold' }}>规则详细统计</span>}
            style={{ marginTop: '16px', borderRadius: '12px' }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              padding: '16px 24px',
              borderRadius: '12px',
              marginBottom: '16px',
              color: 'white'
            }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>
                💡 规则统计
              </div>
              <div style={{ fontSize: '13px', lineHeight: '1.6', opacity: 0.85 }}>
                展示该维度下各规则的详细指标表现。
                {dimensionData.rule_count && ` 共 ${dimensionData.rule_count} 条规则。`}
              </div>
            </div>
            
            <div className="space-y-4">
              {dimensionData.rule_stats.map((rule, index) => (
                <div 
                  key={rule.rule_code || index}
                  style={{
                    background: '#fafafa',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: COLORS[index % COLORS.length]
                      }} />
                      <span style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>
                        {rule.rule_name || rule.rule_code}
                      </span>
                    </div>
                    {rule.accuracy && (
                      <span style={{ 
                        fontSize: '24px', 
                        fontWeight: 'bold', 
                        color: getColorByRate(parseFloat(rule.accuracy?.replace('%', '') || 0) / 100)
                      }}>
                        {rule.accuracy}
                      </span>
                    )}
                  </div>
                  
                  {/* 动态展示存在的指标卡片 */}
                  {(() => {
                    const metricCards = [];
                    
                    if (rule.risk_precision) {
                      metricCards.push(
                        <div key="risk_precision" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>有风险精确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
                            {rule.risk_precision}
                          </div>
                        </div>
                      );
                    }
                    
                    if (rule.no_risk_precision) {
                      metricCards.push(
                        <div key="no_risk_precision" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>无风险精确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                            {rule.no_risk_precision}
                          </div>
                        </div>
                      );
                    }
                    
                    if (rule.risk_recall) {
                      metricCards.push(
                        <div key="risk_recall" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>有风险召回率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#faad14' }}>
                            {rule.risk_recall}
                          </div>
                        </div>
                      );
                    }
                    
                    if (rule.no_risk_recall) {
                      metricCards.push(
                        <div key="no_risk_recall" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>无风险召回率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#722ed1' }}>
                            {rule.no_risk_recall}
                          </div>
                        </div>
                      );
                    }
                    
                    if (rule.accuracy) {
                      metricCards.push(
                        <div key="accuracy" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>准确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#13c2c2' }}>
                            {rule.accuracy}
                          </div>
                        </div>
                      );
                    }
                    
                    if (metricCards.length === 0) return null;
                    
                    const cols = metricCards.length <= 3 ? metricCards.length : 5;
                    return (
                      <div className={`grid gap-3`} style={{ 
                        fontSize: '13px',
                        gridTemplateColumns: `repeat(${Math.min(cols, metricCards.length)}, 1fr)`
                      }}>
                        {metricCards}
                      </div>
                    );
                  })()}
                  
                  {/* 动态展示存在的总数信息 */}
                  {(() => {
                    const stats = [];
                    
                    if (rule.total !== undefined) {
                      stats.push(
                        <span key="total">总数: <strong style={{ color: '#262626' }}>{rule.total}</strong></span>
                      );
                    }
                    
                    if (rule.machine_risky !== undefined) {
                      stats.push(
                        <span key="machine_risky">机审有风险: <strong style={{ color: '#52c41a' }}>{rule.machine_risky}</strong></span>
                      );
                    }
                    
                    if (rule.machine_non_risky !== undefined) {
                      stats.push(
                        <span key="machine_non_risky">机审无风险: <strong style={{ color: '#1890ff' }}>{rule.machine_non_risky}</strong></span>
                      );
                    }
                    
                    if (rule.expected_risky !== undefined) {
                      stats.push(
                        <span key="expected_risky">人审有风险: <strong style={{ color: '#fa541c' }}>{rule.expected_risky}</strong></span>
                      );
                    }
                    
                    if (rule.expected_non_risky !== undefined) {
                      stats.push(
                        <span key="expected_non_risky">人审无风险: <strong style={{ color: '#722ed1' }}>{rule.expected_non_risky}</strong></span>
                      );
                    }
                    
                    if (stats.length === 0) return null;
                    
                    return (
                      <div className="flex items-center gap-4 mt-3" style={{ fontSize: '13px', color: '#8c8c8c' }}>
                        {stats}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* 自定义维度的实例统计（如变更项等） */}
        {dimensionData.instance_stats && dimensionData.instance_stats.length > 0 && (
          <Card
            title={<span style={{ fontSize: '16px', fontWeight: 'bold' }}>实例详细统计</span>}
            style={{ marginTop: '16px', borderRadius: '12px' }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              padding: '16px 24px',
              borderRadius: '12px',
              marginBottom: '16px',
              color: 'white'
            }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>
                💡 维度说明
              </div>
              <div style={{ fontSize: '13px', lineHeight: '1.6', opacity: 0.85 }}>
                按实例（如变更项）分组统计各项指标。
                {dimensionData.instance_count && ` 共 ${dimensionData.instance_count} 个实例参与计算。`}
              </div>
            </div>
            
            <div className="space-y-4">
              {dimensionData.instance_stats.map((instance, index) => (
                <div 
                  key={instance.instance_code || index}
                  style={{
                    background: '#fafafa',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: COLORS[index % COLORS.length]
                      }} />
                      <span style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>
                        {instance.instance_name || instance.instance_code}
                      </span>
                    </div>
                    <span style={{ 
                      fontSize: '24px', 
                      fontWeight: 'bold', 
                      color: getColorByRate(parseFloat(instance.accuracy?.replace('%', '') || 0) / 100)
                    }}>
                      {instance.accuracy || '-'}
                    </span>
                  </div>
                  
                  {/* 动态展示存在的总数信息 */}
                  {(() => {
                    const stats = [];
                    if (instance.total !== undefined) stats.push(<span key="total">总数: <strong style={{ color: '#262626' }}>{instance.total}</strong></span>);
                    if (instance.machine_risky !== undefined) stats.push(<span key="machine_risky">机审有风险: <strong style={{ color: '#52c41a' }}>{instance.machine_risky}</strong></span>);
                    if (instance.machine_non_risky !== undefined) stats.push(<span key="machine_non_risky">机审无风险: <strong style={{ color: '#1890ff' }}>{instance.machine_non_risky}</strong></span>);
                    if (instance.expected_risky !== undefined) stats.push(<span key="expected_risky">人审有风险: <strong style={{ color: '#fa541c' }}>{instance.expected_risky}</strong></span>);
                    if (instance.expected_non_risky !== undefined) stats.push(<span key="expected_non_risky">人审无风险: <strong style={{ color: '#722ed1' }}>{instance.expected_non_risky}</strong></span>);
                    
                    if (stats.length === 0) return null;
                    return (
                      <div className="flex items-center gap-4 mt-3" style={{ fontSize: '13px', color: '#8c8c8c' }}>
                        {stats}
                      </div>
                    );
                  })()}
                  
                  {/* 实例下的规则平均指标 */}
                  {instance.rule_avg && (
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px dashed #d9d9d9' }}>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#595959', marginBottom: '12px' }}>
                        🎯 规则平均指标
                      </div>
                      <div className="grid grid-cols-5 gap-2" style={{ fontSize: '12px' }}>
                        <div style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>有风险精确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
                            {instance.rule_avg.risk_precision || '0.00%'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>无风险精确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                            {instance.rule_avg.no_risk_precision || '0.00%'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>有风险召回率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#faad14' }}>
                            {instance.rule_avg.risk_recall || '0.00%'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>无风险召回率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#722ed1' }}>
                            {instance.rule_avg.no_risk_recall || '0.00%'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>准确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#13c2c2' }}>
                            {instance.rule_avg.accuracy || '0.00%'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* 实例下的规则统计 */}
                  {instance.rule_stats && instance.rule_stats.length > 0 && (
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px dashed #d9d9d9' }}>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#595959', marginBottom: '12px' }}>
                        📋 规则明细 ({instance.rule_stats.length}条)
                      </div>
                      <div className="space-y-3">
                        {instance.rule_stats.map((rule, ruleIndex) => (
                          <div 
                            key={rule.rule_code || ruleIndex}
                            style={{
                              background: 'white',
                              padding: '12px 16px',
                              borderRadius: '8px',
                              border: '1px solid #f0f0f0'
                            }}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div style={{
                                  width: '6px',
                                  height: '6px',
                                  borderRadius: '50%',
                                  background: COLORS[ruleIndex % COLORS.length]
                                }} />
                                <span style={{ fontSize: '14px', fontWeight: 500, color: '#262626' }}>
                                  {rule.rule_name || rule.rule_code}
                                </span>
                              </div>
                              {rule.accuracy && (
                                <span style={{ 
                                  fontSize: '18px', 
                                  fontWeight: 'bold', 
                                  color: getColorByRate(parseFloat(rule.accuracy?.replace('%', '') || 0) / 100)
                                }}>
                                  {rule.accuracy}
                                </span>
                              )}
                            </div>
                            
                            {/* 动态展示存在的指标 */}
                            {(() => {
                              const metrics = [];
                              if (rule.risk_precision) metrics.push({ label: '有风险精确率', value: rule.risk_precision, color: '#52c41a' });
                              if (rule.no_risk_precision) metrics.push({ label: '无风险精确率', value: rule.no_risk_precision, color: '#1890ff' });
                              if (rule.risk_recall) metrics.push({ label: '有风险召回率', value: rule.risk_recall, color: '#faad14' });
                              if (rule.no_risk_recall) metrics.push({ label: '无风险召回率', value: rule.no_risk_recall, color: '#722ed1' });
                              if (rule.accuracy) metrics.push({ label: '准确率', value: rule.accuracy, color: '#13c2c2' });
                              
                              if (metrics.length === 0) return null;
                              
                              const cols = metrics.length <= 3 ? metrics.length : 5;
                              return (
                                <div className="grid gap-2 mt-2" style={{ 
                                  fontSize: '12px',
                                  gridTemplateColumns: `repeat(${Math.min(cols, metrics.length)}, 1fr)`
                                }}>
                                  {metrics.map((m, i) => (
                                    <div key={i} style={{ textAlign: 'center', padding: '4px' }}>
                                      <div style={{ color: '#8c8c8c', marginBottom: '2px' }}>{m.label}</div>
                                      <div style={{ fontWeight: 'bold', color: m.color }}>{m.value}</div>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    );
  };

  // 渲染分组指标
  const renderGroupMetrics = (groupMetrics) => {
    if (!groupMetrics || Object.keys(groupMetrics).length === 0) return null;

    return (
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', color: '#1a1a1a' }}>
          📂 分组维度指标
        </h2>
        
        <div style={{
          background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
          padding: '16px 24px',
          borderRadius: '12px',
          marginBottom: '16px',
          color: '#333'
        }}>
          <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
            💡 维度说明
          </div>
          <div style={{ fontSize: '13px', lineHeight: '1.6', opacity: 0.85 }}>
            按规则分组（如变更项）统计各项指标，支持多级嵌套分组结构。共 {Object.keys(groupMetrics).length} 个分组。
          </div>
        </div>

        <Row gutter={[16, 16]}>
          {Object.entries(groupMetrics).map(([groupName, groupData], index) => (
            <Col span={12} key={groupName}>
              <Card
                style={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  height: '100%'
                }}
                bodyStyle={{ padding: '20px' }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: COLORS[index % COLORS.length]
                  }} />
                  <span style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>
                    {groupName}
                  </span>
                </div>
                
                {/* 准确率 */}
                <div className="flex items-center justify-between mb-3">
                  <span style={{ fontSize: '14px', color: '#666' }}>准确率</span>
                  <span style={{ 
                    fontSize: '20px', 
                    fontWeight: 'bold', 
                    color: getColorByRate(groupData.accuracy_value || 0)
                  }}>
                    {groupData.accuracy || '-'}
                  </span>
                </div>
                <Progress 
                  percent={(groupData.accuracy_value || 0) * 100} 
                  strokeColor={getColorByRate(groupData.accuracy_value || 0)}
                  trailColor="#e8e8e8"
                  strokeWidth={8}
                  showInfo={false}
                />
                
                {/* 详细指标 */}
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <div style={{ background: '#f6ffed', padding: '12px', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#52c41a', marginBottom: '4px' }}>有风险精确率</div>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>{groupData.risk_precision || '-'}</div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ background: '#e6f7ff', padding: '12px', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#1890ff', marginBottom: '4px' }}>无风险精确率</div>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>{groupData.no_risk_precision || '-'}</div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ background: '#fff7e6', padding: '12px', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#fa8c16', marginBottom: '4px' }}>有风险召回率</div>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>{groupData.risk_recall || '-'}</div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ background: '#f9f0ff', padding: '12px', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#722ed1', marginBottom: '4px' }}>无风险召回率</div>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>{groupData.no_risk_recall || '-'}</div>
                      </div>
                    </Col>
                  </Row>
                  
                  {/* 动态展示存在的统计数据 */}
                  {(() => {
                    const stats = [];
                    if (groupData.total !== undefined) stats.push(<span key="total">总数: <strong style={{ color: '#262626' }}>{groupData.total}</strong></span>);
                    if (groupData.machine_risky !== undefined) stats.push(<span key="machine_risky">机审有风险: <strong style={{ color: '#ff4d4f' }}>{groupData.machine_risky}</strong></span>);
                    if (groupData.machine_non_risky !== undefined) stats.push(<span key="machine_non_risky">机审无风险: <strong style={{ color: '#52c41a' }}>{groupData.machine_non_risky}</strong></span>);
                    
                    if (stats.length === 0) return null;
                    return (
                      <div className="flex items-center gap-4 mt-3" style={{ fontSize: '12px', color: '#8c8c8c' }}>
                        {stats}
                      </div>
                    );
                  })()}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  // 渲染雷达图
  const renderRadarChart = (presetMetrics) => {
    if (!presetMetrics?.document) return null;

    const doc = presetMetrics.document;
    const radarData = [
      { metric: '有风险精确率', value: (doc.risk_precision_value || 0) * 100 },
      { metric: '无风险精确率', value: (doc.no_risk_precision_value || 0) * 100 },
      { metric: '有风险召回率', value: (doc.risk_recall_value || 0) * 100 },
      { metric: '无风险召回率', value: (doc.no_risk_recall_value || 0) * 100 },
      { metric: '准确率', value: (doc.accuracy_value || 0) * 100 }
    ];

    return (
      <Card 
        title={<span style={{ fontSize: '18px', fontWeight: 'bold' }}>指标雷达图</span>}
        style={{ borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '24px' }}
        bodyStyle={{ padding: '24px' }}
      >
        <ResponsiveContainer width="100%" height={320}>
          <RadarChart data={radarData}>
            <PolarGrid stroke="#e0e0e0" />
            <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12 }} />
            <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 11 }} />
            <Radar 
              name="指标值" 
              dataKey="value" 
              stroke="#5B8FF9" 
              fill="#5B8FF9" 
              fillOpacity={0.6}
              strokeWidth={2}
            />
            <RechartsTooltip 
              formatter={(value) => [`${value.toFixed(2)}%`, '指标值']}
              contentStyle={{ 
                borderRadius: '8px', 
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
            />
          </RadarChart>
        </ResponsiveContainer>
      </Card>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)' }}>
      {/* 顶部导航栏 */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        padding: '16px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>返回</Button>
          <div style={{ height: '24px', width: '1px', background: '#e8e8e8' }} />
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#262626' }}>
            评测结果统计
          </h2>
          {taskName && (
            <span style={{ color: '#666', fontSize: '14px' }}>- {taskName}</span>
          )}
        </div>
      </div>

      {/* 主内容区域 */}
      <div style={{ padding: '24px 32px' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
            <Spin size="large" tip="加载中..." />
          </div>
        ) : !statsData ? (
          <Card style={{ borderRadius: '16px' }}>
            <Empty description="暂无统计数据" />
          </Card>
        ) : (
          <>
            {/* 执行信息摘要 */}
            {executionInfo && (
              <Card 
                style={{ 
                  marginBottom: '24px', 
                  borderRadius: '16px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
                bodyStyle={{ padding: '32px' }}
              >
                <Row gutter={[24, 24]}>
                  <Col span={6}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '8px' }}>执行ID</div>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: '#1890ff', fontFamily: 'monospace' }}>
                        {executionInfo.id}
                      </div>
                    </div>
                  </Col>
                  <Col span={6}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '8px' }}>任务名称</div>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>
                        {executionInfo.taskName || taskName || '-'}
                      </div>
                    </div>
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title={<span style={{ fontSize: '14px', color: '#8c8c8c' }}>单据总数</span>}
                      value={(() => {
                        // 优先取 document 维度的总数
                        if (statsData.presetMetrics?.document?.total) {
                          return statsData.presetMetrics.document.total;
                        }
                        // 如果没有 document，取第一个自定义维度的总数
                        const customMetrics = statsData.presetMetrics || {};
                        const firstCustomKey = Object.keys(customMetrics).find(key => 
                          key !== 'document' && key !== 'rule_avg' && key !== 'rule_detail' && key !== 'group_metrics'
                        );
                        if (firstCustomKey && customMetrics[firstCustomKey]?.total) {
                          return customMetrics[firstCustomKey].total;
                        }
                        return executionInfo.totalCaseCount || 0;
                      })()}
                      suffix="个"
                      valueStyle={{ fontSize: '32px', fontWeight: 'bold', color: '#1890ff' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title={<span style={{ fontSize: '14px', color: '#8c8c8c' }}>准确率</span>}
                      value={(() => {
                        // 优先取 document 维度的准确率
                        if (statsData.presetMetrics?.document?.accuracy) {
                          return statsData.presetMetrics.document.accuracy;
                        }
                        // 如果没有 document，取第一个自定义维度的准确率
                        const customMetrics = statsData.presetMetrics || {};
                        const firstCustomKey = Object.keys(customMetrics).find(key => 
                          key !== 'document' && key !== 'rule_avg' && key !== 'rule_detail' && key !== 'group_metrics'
                        );
                        if (firstCustomKey && customMetrics[firstCustomKey]?.accuracy) {
                          return customMetrics[firstCustomKey].accuracy;
                        }
                        return '-';
                      })()}
                      valueStyle={{ fontSize: '32px', fontWeight: 'bold', color: '#52c41a' }}
                    />
                  </Col>
                </Row>
              </Card>
            )}

            {/* 预设指标 - 单据维度 */}
            {statsData.presetMetrics?.document && 
              renderDimensionMetrics(statsData.presetMetrics.document, 'document', DIMENSION_NAMES.document)}

            {/* 预设指标 - 规则明细维度 */}
            {statsData.presetMetrics?.rule_detail && 
              renderDimensionMetrics(statsData.presetMetrics.rule_detail, 'rule_detail', DIMENSION_NAMES.rule_detail)}

            {/* 预设指标 - 规则平均维度 */}
            {statsData.presetMetrics?.rule_avg && 
              renderDimensionMetrics(statsData.presetMetrics.rule_avg, 'rule_avg', DIMENSION_NAMES.rule_avg)}

            {/* 预设指标 - 自定义维度（除了预设的3个维度外的其他维度） */}
            {statsData.presetMetrics && Object.entries(statsData.presetMetrics)
              .filter(([key]) => !['document', 'rule_detail', 'rule_avg', 'group_metrics'].includes(key))
              .map(([key, data]) => {
                // 当存在预设维度时，使用定义的维度key（而不是instance_name）
                let displayName = key;
                
                // 将技术标识符转换为友好的显示名称
                if (!/[\u4e00-\u9fa5]/.test(key)) {
                  // 如果不包含中文，进行下划线转换
                  displayName = key
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase());
                }
                
                return (
                  <div key={key}>
                    {renderDimensionMetrics(data, key, displayName)}
                  </div>
                );
              })
            }

            {/* 分组指标 */}
            {renderGroupMetrics(statsData.presetMetrics?.group_metrics)}

            {/* 雷达图 */}
            {renderRadarChart(statsData.presetMetrics)}

            {/* 摘要信息 */}
            {statsData.summary && (
              <Card
                title={<span style={{ fontSize: '18px', fontWeight: 'bold' }}>📋 摘要信息</span>}
                style={{ borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                bodyStyle={{ padding: '24px' }}
              >
                <Row gutter={[16, 16]}>
                  {Object.entries(statsData.summary).map(([key, value], index) => (
                    <Col span={8} key={key}>
                      <div style={{
                        background: '#fafafa',
                        padding: '16px',
                        borderRadius: '12px',
                        border: '1px solid #f0f0f0'
                      }}>
                        <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>{key}</div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#262626' }}>{value}</div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AutoReviewResultStats;
